import { Pencil } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'

/**
 * Text you click to change, like the dashboard's inline fields (po-inline-edit): Enter or leaving
 * the field keeps it, Escape puts it back. With no value yet it starts as an open field (USA-1345).
 * Changes reach the form as they are typed, so a submit right after typing never waits on a blur.
 */
export function InlineEditField({
  value,
  onChange,
  label,
  placeholder,
  maxLength,
  autoComplete,
}: {
  value: string
  onChange: (value: string) => void
  /** What the field is, for screen readers. */
  label: string
  placeholder?: string
  maxLength?: number
  autoComplete?: string
}) {
  const [editing, setEditing] = useState(!value)
  const [opened, setOpened] = useState(false)
  const [original, setOriginal] = useState(value)

  const close = () => {
    const next = value.trim()
    if (next) onChange(next)
    else if (original) onChange(original)
    else return
    setEditing(false)
  }

  if (!editing && value) {
    return (
      <button
        aria-label={`${label}: ${value}. Change`}
        className="hover:bg-muted focus-visible:outline-usa-blue -mx-2 inline-flex min-h-9 max-w-full items-center gap-1.5 rounded-md px-2 text-sm font-medium focus-visible:outline-2"
        onClick={() => {
          setOriginal(value)
          setOpened(true)
          setEditing(true)
        }}
        title="Click to edit"
        type="button"
      >
        <span className="min-w-0 truncate">{value}</span>
        <Pencil aria-hidden="true" className="text-muted-foreground size-3.5 shrink-0" />
      </button>
    )
  }
  return (
    <input
      aria-label={label}
      autoComplete={autoComplete}
      // Opened by a click on the value: type straight away. An empty field waits for the customer.
      autoFocus={opened}
      className="focus:border-usa-blue focus:ring-usa-blue/15 bg-background h-9 w-full rounded-md border px-3 text-sm outline-none focus:ring-2"
      // A panel's Escape leaves an open field to this one (see CommentsPanel).
      data-inline-edit=""
      maxLength={maxLength}
      onBlur={close}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          close()
        } else if (event.key === 'Escape' && original) {
          event.preventDefault()
          event.stopPropagation()
          onChange(original)
          setEditing(false)
        }
      }}
      placeholder={placeholder}
      required
      value={value}
    />
  )
}
